"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { useRouter } from "next/navigation";
import { CTAButton, SectionHeader, EntityDisplay } from "fansunited-sports-ui";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { BettingOdds } from "@/features/odds/components/BettingOdds";
import type { AdaptedEventCompetitor } from "@/utilities/data/team/types";
import { formatDateDDMM, formatUpcomingMatchTime } from "@/utilities/formatting/date";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useTeamPageData } from "@/views/team/context/TeamPageDataContext";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
interface MatchHighlightProps {
  type: "latest" | "upcoming";
}
function CompetitorRow(t0) {
  const $ = _c(15);
  const {
    competitor,
    score
  } = t0;
  const t1 = competitor.assets?.logo;
  const t2 = competitor.country?.code;
  const t3 = competitor.country?.flag;
  const t4 = competitor.metadata?.branding?.primaryColor;
  const t5 = competitor.metadata?.branding?.secondaryColor;
  let t6;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3 || $[3] !== t4 || $[4] !== t5) {
    t6 = <TeamLogo src={t1} size="sm" countryCode={t2} countryFlag={t3} primaryColor={t4} secondaryColor={t5} />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      container: "flex-1",
      imageSize: "md",
      typography: {
        name: "font-semibold text-foreground text-sm"
      }
    };
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== competitor.name || $[8] !== t6) {
    t8 = <EntityDisplay name={competitor.name} image={t6} styles={t7} />;
    $[7] = competitor.name;
    $[8] = t6;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== score) {
    t9 = score != null && <span className="text-lg font-bold tabular-nums text-foreground">{score}</span>;
    $[10] = score;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t8 || $[13] !== t9) {
    t10 = <div className="flex items-center justify-between">{t8}{t9}</div>;
    $[12] = t8;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  return t10;
}
export const MatchHighlight: React.FC<MatchHighlightProps> = t0 => {
  const $ = _c(72);
  const {
    type
  } = t0;
  const isLatest = type === "latest";
  const {
    team,
    getNextMatch,
    getPreviousMatch
  } = useTeamPageData();
  const nativeTeamId = team.id;
  const labels = useLabels().matches;
  const nav = useNavigation();
  const router = useRouter();
  const {
    hasOdds
  } = useTenantSettings();
  let matchHref;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== getNextMatch || $[1] !== getPreviousMatch || $[2] !== hasOdds || $[3] !== isLatest || $[4] !== labels.latestResult || $[5] !== labels.nextMatch || $[6] !== labels.noLatestResultSubtitle || $[7] !== labels.noLatestResultTitle || $[8] !== labels.noNextMatchSubtitle || $[9] !== labels.noNextMatchTitle || $[10] !== labels.viewAll || $[11] !== nativeTeamId || $[12] !== nav || $[13] !== router) {
    t9 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const event = isLatest ? getPreviousMatch() : getNextMatch();
      if (!event) {
        const headerLabel = isLatest ? labels.latestResult : labels.nextMatch;
        const emptyTitle = isLatest ? labels.noLatestResultTitle : labels.noNextMatchTitle;
        const emptySubtitle = isLatest ? labels.noLatestResultSubtitle : labels.noNextMatchSubtitle;
        let t10;
        if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
          t10 = {
            typography: {
              name: "text-sm uppercase font-semibold text-foreground"
            }
          };
          $[24] = t10;
        } else {
          t10 = $[24];
        }
        let t11;
        if ($[25] !== headerLabel) {
          t11 = {
            name: headerLabel,
            styles: t10
          };
          $[25] = headerLabel;
          $[26] = t11;
        } else {
          t11 = $[26];
        }
        let t12;
        if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
          t12 = {
            container: "mb-2"
          };
          $[27] = t12;
        } else {
          t12 = $[27];
        }
        let t13;
        if ($[28] !== t11) {
          t13 = <SectionHeader entity={t11} styles={t12} />;
          $[28] = t11;
          $[29] = t13;
        } else {
          t13 = $[29];
        }
        let t14;
        if ($[30] !== emptyTitle) {
          t14 = <p className="text-base font-medium">{emptyTitle}</p>;
          $[30] = emptyTitle;
          $[31] = t14;
        } else {
          t14 = $[31];
        }
        let t15;
        if ($[32] !== emptySubtitle) {
          t15 = <p className="mt-1 text-sm opacity-70">{emptySubtitle}</p>;
          $[32] = emptySubtitle;
          $[33] = t15;
        } else {
          t15 = $[33];
        }
        let t16;
        if ($[34] !== t14 || $[35] !== t15) {
          t16 = <div className="match-card-surface flex flex-1 flex-col items-center justify-center p-8 text-center">{t14}{t15}</div>;
          $[34] = t14;
          $[35] = t15;
          $[36] = t16;
        } else {
          t16 = $[36];
        }
        let t17;
        if ($[37] !== t13 || $[38] !== t16) {
          t17 = <div className="flex h-full flex-col">{t13}{t16}</div>;
          $[37] = t13;
          $[38] = t16;
          $[39] = t17;
        } else {
          t17 = $[39];
        }
        t9 = t17;
        break bb0;
      }
      matchHref = nav.match(event.fansUnitedMatchId || event.id);
      const kickoffDate = new Date(event.date);
      const headerLabel_0 = isLatest ? labels.latestResult : labels.nextMatch;
      const primaryCtaText = labels.viewAll;
      let t10;
      if ($[40] !== nativeTeamId || $[41] !== nav) {
        t10 = nav.team(nativeTeamId);
        $[40] = nativeTeamId;
        $[41] = nav;
        $[42] = t10;
      } else {
        t10 = $[42];
      }
      const primaryCtaHref = `${t10}#matches`;
      const statusText = isLatest ? formatDateDDMM(kickoffDate) : formatUpcomingMatchTime(kickoffDate);
      let t11;
      if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
        t11 = {
          typography: {
            name: "text-sm uppercase font-semibold text-foreground"
          }
        };
        $[43] = t11;
      } else {
        t11 = $[43];
      }
      let t12;
      if ($[44] !== headerLabel_0) {
        t12 = {
          name: headerLabel_0,
          styles: t11
        };
        $[44] = headerLabel_0;
        $[45] = t12;
      } else {
        t12 = $[45];
      }
      let t13;
      if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
        t13 = {
          container: "text-sm hover:underline border border-transparent p-0 m-0"
        };
        $[46] = t13;
      } else {
        t13 = $[46];
      }
      let t14;
      if ($[47] !== primaryCtaHref || $[48] !== primaryCtaText) {
        t14 = <CTAButton text={primaryCtaText} href={primaryCtaHref} variant="custom" styles={t13} />;
        $[47] = primaryCtaHref;
        $[48] = primaryCtaText;
        $[49] = t14;
      } else {
        t14 = $[49];
      }
      let t15;
      if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
        t15 = {
          container: "mb-2"
        };
        $[50] = t15;
      } else {
        t15 = $[50];
      }
      if ($[51] !== t12 || $[52] !== t14) {
        t8 = <SectionHeader entity={t12} primaryCta={t14} styles={t15} />;
        $[51] = t12;
        $[52] = t14;
        $[53] = t8;
      } else {
        t8 = $[53];
      }
      t6 = "match-card-surface overflow-hidden shadow-md";
      t7 = !isLatest ? {
        onClick: () => router.push(matchHref),
        role: "button",
        tabIndex: 0
      } : {};
      t1 = "p-4 space-y-2 text-card-foreground";
      t2 = <span className="text-xs font-medium text-muted-foreground uppercase">{statusText}</span>;
      t3 = <CompetitorRow competitor={event.competitorOne} score={isLatest && event.score ? event.score.fullTime.competitorOne || "0" : undefined} />;
      t4 = <CompetitorRow competitor={event.competitorTwo} score={isLatest && event.score ? event.score.fullTime.competitorTwo || "0" : undefined} />;
      t5 = !isLatest && hasOdds && <div className="pt-2 border-t border-border"><BettingOdds matchId={event.fansUnitedMatchId || event.id} markets={["1x2"]} market="FT_1X2" kickoffAt={kickoffDate.toISOString()} /></div>;
    }
    $[0] = getNextMatch;
    $[1] = getPreviousMatch;
    $[2] = hasOdds;
    $[3] = isLatest;
    $[4] = labels.latestResult;
    $[5] = labels.nextMatch;
    $[6] = labels.noLatestResultSubtitle;
    $[7] = labels.noLatestResultTitle;
    $[8] = labels.noNextMatchSubtitle;
    $[9] = labels.noNextMatchTitle;
    $[10] = labels.viewAll;
    $[11] = nativeTeamId;
    $[12] = nav;
    $[13] = router;
    $[14] = matchHref;
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    matchHref = $[14];
    t1 = $[15];
    t2 = $[16];
    t3 = $[17];
    t4 = $[18];
    t5 = $[19];
    t6 = $[20];
    t7 = $[21];
    t8 = $[22];
    t9 = $[23];
  }
  if (t9 !== Symbol.for("react.early_return_sentinel")) {
    return t9;
  }
  let t10;
  if ($[54] !== isLatest || $[55] !== labels.matchDetails || $[56] !== matchHref) {
    t10 = isLatest && <div className="pt-2 border-t border-border"><CTAButton text={labels.matchDetails} href={matchHref} variant="outline" styles={{
        container: "bg-purple-600 p-1 text-sm border-none bg-muted"
      }} /></div>;
    $[54] = isLatest;
    $[55] = labels.matchDetails;
    $[56] = matchHref;
    $[57] = t10;
  } else {
    t10 = $[57];
  }
  let t11;
  if ($[58] !== t1 || $[59] !== t10 || $[60] !== t2 || $[61] !== t3 || $[62] !== t4 || $[63] !== t5) {
    t11 = <div className={t1}>{t2}{t3}{t4}{t5}{t10}</div>;
    $[58] = t1;
    $[59] = t10;
    $[60] = t2;
    $[61] = t3;
    $[62] = t4;
    $[63] = t5;
    $[64] = t11;
  } else {
    t11 = $[64];
  }
  let t12;
  if ($[65] !== t11 || $[66] !== t6 || $[67] !== t7) {
    t12 = <div className={t6} {...t7}>{t11}</div>;
    $[65] = t11;
    $[66] = t6;
    $[67] = t7;
    $[68] = t12;
  } else {
    t12 = $[68];
  }
  let t13;
  if ($[69] !== t12 || $[70] !== t8) {
    t13 = <div>{t8}{t12}</div>;
    $[69] = t12;
    $[70] = t8;
    $[71] = t13;
  } else {
    t13 = $[71];
  }
  return t13;
};